// Firebase configuration and initialization
import { initializeApp, FirebaseApp, getApps } from "firebase/app";
import {
  getAuth,
  Auth,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import { initializeFirestore, Firestore } from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFunctions, Functions } from "firebase/functions";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY?.trim(),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim(),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET?.trim(),
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim(),
  appId: import.meta.env.VITE_FIREBASE_APP_ID?.trim(),
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID?.trim(),
};

// Determine if Firebase is properly configured
export const isFirebaseConfigured = !!(
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId &&
  // Ensure it's not a placeholder/empty string
  firebaseConfig.apiKey.length > 10
);

// Initialize Firebase app - only with valid credentials
const app: FirebaseApp = isFirebaseConfigured
  ? initializeApp(firebaseConfig)
  : (getApps()[0] ??
    initializeApp({
      // Use a minimal but structurally valid mock config so Firebase SDK
      // doesn't throw on getAuth/getFirestore calls; actual network calls
      // will fail (caught by our error handlers) but the app can render.
      apiKey: "AIzaSy-placeholder-not-real",
      authDomain: "placeholder.firebaseapp.com",
      projectId: "placeholder-project",
      storageBucket: "placeholder-project.appspot.com",
      messagingSenderId: "000000000000",
      appId: "1:000000000000:web:0000000000000000",
    }));

export { app };

// Initialize Firebase services (safe even with mock config)
export const auth: Auth = getAuth(app);
export const db: Firestore = initializeFirestore(app, {
  // Force long polling (skip WebSocket detection delay on mobile networks)
  experimentalForceLongPolling: true,
});
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, "asia-south1");

// Connect to Firebase Emulators in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  const { connectFirestoreEmulator } = await import('firebase/firestore');
  const { connectAuthEmulator } = await import('firebase/auth');
  const { connectFunctionsEmulator } = await import('firebase/functions');
  const { connectStorageEmulator } = await import('firebase/storage');

  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('🔥 Connected to Firebase Emulators');
  } catch (error) {
    console.warn('Firebase Emulators already connected or unavailable');
  }
}

// Persist auth session across browser reloads
export const authPersistenceReady: Promise<void> = isFirebaseConfigured
  ? setPersistence(auth, browserLocalPersistence).catch(() => {
      /* silently ignore if localStorage unavailable */
    })
  : Promise.resolve();

// Initialize analytics only if supported and configured
export const initAnalytics = async () => {
  if (!isFirebaseConfigured) return null;
  const analytics = await import("firebase/analytics");
  if (await analytics.isSupported()) {
    return analytics.getAnalytics(app);
  }
  return null;
};
